import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {useRecordEvent} from "aws-rum-react";
import {Button, Card, Spinner} from "react-bootstrap";
import {ConfirmationModal, PageBody, PageHeader, PageLayout} from "../../components";
import {ImageCanvas} from "../../features";
import {useConfirmNavigation, useUploadedImages} from "../../hooks";
import {IDropFileModel} from "../../types/common";
import {imageMatcherUploadPageURL, selectedImageMatcherURL} from "../../utils/imageMatcherUtil";
import {ImageMatcherFilter} from "../../features/PopoverFilters/ImageMatcherFilter";

const ImageMatcherUploadedPage: React.FC = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const recordEvent = useRecordEvent();
    const navigate = useNavigate();
    const { handleGetUploadedImages, handleSetUploadedImages, handleClearUploadedImages } = useUploadedImages();
    const dropFilesOnState: IDropFileModel[] = location.state;
    const [openedImages, setOpenedImages] = useState<string[]>([]);
    const [uploadedImages, setUploadedImages] = useState<IDropFileModel[]>([]);
    const [isAllImagesProcessed, setIsAllImagesProcessed] = useState<boolean>(false);
    const {isModalOpen, confirmNavigation, cancelNavigation, setIsDirty} = useConfirmNavigation();

    useEffect(() => {
        if (dropFilesOnState.length === 0) {
            handleClearUploadedImages();
            navigate(imageMatcherUploadPageURL);
        } else {
            setUploadedImages(dropFilesOnState);
        }
    }, [dropFilesOnState, handleGetUploadedImages, handleSetUploadedImages, navigate, uploadedImages]);

    const openInNewTabs = () => {
        let isNotBlocked = true;
        uploadedImages.forEach((dropFile) => {
            if (isNotBlocked && dropFile.id) {
                handleClickOpenImage(dropFile.id);
                let newWin = window.open(`${window.location}/result/${dropFile.id}`, "_blank");
                if (!newWin || newWin.closed || typeof newWin.closed === "undefined") {
                    alert(t("DropZone.hintMultipleTabs"));
                    isNotBlocked = false;
                }
            }
        });

        recordEvent("OpenAllResultsInNewTabs", { user_interaction: "click", resultCount: uploadedImages.length });
    };

    const handleClickOpenImage = (imageId: string) => {
        if (!openedImages.includes(imageId)) {
            setOpenedImages((prev) => [...prev, imageId]);
        }
    };

    const handleProcessedImage = async (image: IDropFileModel, index: number) => {
        setUploadedImages(prevImages => {
            const updatedImages = [...prevImages];
            if (index >= 0 && index < updatedImages.length) {
                updatedImages[index] = { ...image };
            }
            return updatedImages;
        });
        await new Promise(resolve => setTimeout(resolve, 0));
        handleSetUploadedImages(uploadedImages);
        if (uploadedImages.length === 1) {
            navigate(selectedImageMatcherURL(image.id));
        }
        setIsDirty(true);
        setIsAllImagesProcessed(uploadedImages.filter(x => !x.id).length === 0);
    };

    return (
        <PageLayout>
            <ConfirmationModal
                show={isModalOpen}
                title={t("ConfirmationModal.ConfirmationTitle")}
                body={t("ConfirmationModal.ConfirmationMessage")}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
            />
            <PageHeader
                title={t("ExtractedImages.ImageMatcher")}
                t={t}
                isDisplayBack={true}
                onBackClick={() => navigate(imageMatcherUploadPageURL)}
                right={
                    <div className="d-flex flex-row gap-2">
                        <Button
                            variant="secondary"
                            onClick={openInNewTabs}
                            disabled={!isAllImagesProcessed}>
                            {t("Button.openAllResultsInNewTabs")}
                            {!isAllImagesProcessed && <Spinner animation="border" size="sm" className="ms-2" />}
                        </Button>
                        <ImageMatcherFilter />
                    </div>
                }
            />
            <PageBody t={t}>
                <Card className="border-0 p-3 d-flex flex-row gap-xxl-4 flex-md-wrap justify-content-center">
                    {uploadedImages.map((dropFile, index) => (
                        <ImageCanvas
                            key={index}
                            openedImages={openedImages}
                            setOpenedImages={handleClickOpenImage}
                            dropFile={dropFile}
                            onProcessedImage={(image) => handleProcessedImage(image, index)}
                        />
                    ))}
                </Card>
            </PageBody>
        </PageLayout>
    );
};

export default ImageMatcherUploadedPage;
